/**
 * Speaker Verification using ECAPA-TDNN ONNX model.
 * Extracts 192-dim embeddings and compares via cosine similarity.
 *
 * CRITICAL: The mel spectrogram pre-processing must exactly match
 * SpeechBrain's parameters. See scripts/test_fixtures/mel_spectrogram_params.json
 */

import * as ort from 'onnxruntime-web';
import { getCachedModel, cacheModel } from './ModelCache';
import type { SpeakerVerificationResult, ModelLoadingProgress } from './types';

// Point ONNX Runtime to our public/vad/ directory for WASM files
// Must use absolute URL so it overrides import.meta.url-based resolution in the bundle
ort.env.wasm.wasmPaths = `${window.location.origin}/vad/`;
// Single-threaded WASM (no SharedArrayBuffer / COOP/COEP headers needed)
ort.env.wasm.numThreads = 1;

const EMBEDDING_DIM = 192;
const SAMPLE_RATE = 16000;
const N_FFT = 400;
const HOP_LENGTH = 160;
const N_MELS = 80;
const MODEL_CACHE_KEY = 'ecapa-tdnn-v1';

let session: ort.InferenceSession | null = null;

export async function loadSpeakerModel(
  modelUrl: string,
  onProgress?: (progress: ModelLoadingProgress) => void,
): Promise<void> {
  if (session) return;

  onProgress?.({ model: 'speaker-verification', loaded: false, progress: 0 });

  try {
    onProgress?.({ model: 'speaker-verification', loaded: false, progress: 10 });

    // Load by URL so ONNX Runtime can resolve external data files (.onnx.data)
    session = await ort.InferenceSession.create(modelUrl, {
      executionProviders: ['wasm'],
    });

    onProgress?.({ model: 'speaker-verification', loaded: true, progress: 100 });
    console.log('[SpeakerVerification] Model loaded');
  } catch (error) {
    onProgress?.({
      model: 'speaker-verification',
      loaded: false,
      progress: 0,
      error: String(error),
    });
    throw error;
  }
}

export async function extractEmbedding(audioData: Float32Array): Promise<number[]> {
  if (!session) throw new Error('Speaker verification model not loaded');

  const melFeatures = computeMelSpectrogram(audioData);
  const inputTensor = new ort.Tensor('float32', melFeatures.data, [1, melFeatures.frames, N_MELS]);
  const results = await session.run({ mel_features: inputTensor });
  const embedding = Array.from(results.embedding.data as Float32Array);

  // L2 normalize
  const norm = Math.sqrt(embedding.reduce((sum, v) => sum + v * v, 0));
  return embedding.map(v => v / norm);
}

export function verifySpeaker(
  testEmbedding: number[],
  enrolledEmbeddings: number[][],
  threshold: number = 0.65,
): SpeakerVerificationResult {
  let bestSimilarity = -1;
  let bestMatchIndex = -1;

  for (let i = 0; i < enrolledEmbeddings.length; i++) {
    const similarity = cosineSimilarity(testEmbedding, enrolledEmbeddings[i]);
    if (similarity > bestSimilarity) {
      bestSimilarity = similarity;
      bestMatchIndex = i;
    }
  }

  return {
    isVerified: bestSimilarity >= threshold,
    confidence: bestSimilarity,
    bestMatchIndex,
  };
}

export function destroySpeakerModel(): void {
  session?.release();
  session = null;
}

// ------- Mel Spectrogram -------

interface MelResult {
  data: Float32Array;
  frames: number;
}

function computeMelSpectrogram(audio: Float32Array): MelResult {
  const numFrames = Math.floor((audio.length - N_FFT) / HOP_LENGTH) + 1;
  if (numFrames <= 0) throw new Error('Audio too short for mel spectrogram');

  const melFilterBank = createMelFilterBank(N_FFT, N_MELS, SAMPLE_RATE);
  const melData = new Float32Array(numFrames * N_MELS);
  const window = hanningWindow(N_FFT);

  for (let i = 0; i < numFrames; i++) {
    const start = i * HOP_LENGTH;
    const frame = new Float32Array(N_FFT);

    for (let j = 0; j < N_FFT; j++) {
      frame[j] = (audio[start + j] || 0) * window[j];
    }

    const fft = realFFT(frame);
    const powerSpectrum = new Float32Array(N_FFT / 2 + 1);
    for (let j = 0; j <= N_FFT / 2; j++) {
      const re = fft[j * 2];
      const im = fft[j * 2 + 1];
      powerSpectrum[j] = re * re + im * im;
    }

    for (let m = 0; m < N_MELS; m++) {
      let energy = 0;
      for (let j = 0; j <= N_FFT / 2; j++) {
        energy += melFilterBank[m * (N_FFT / 2 + 1) + j] * powerSpectrum[j];
      }
      melData[i * N_MELS + m] = Math.log(Math.max(energy, 1e-10));
    }
  }

  return { data: melData, frames: numFrames };
}

function hanningWindow(size: number): Float32Array {
  const window = new Float32Array(size);
  for (let i = 0; i < size; i++) {
    window[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (size - 1)));
  }
  return window;
}

function hzToMel(hz: number): number {
  return 2595 * Math.log10(1 + hz / 700);
}

function melToHz(mel: number): number {
  return 700 * (Math.pow(10, mel / 2595) - 1);
}

function createMelFilterBank(nFft: number, nMels: number, sampleRate: number): Float32Array {
  const numBins = nFft / 2 + 1;
  const filterBank = new Float32Array(nMels * numBins);

  const melMin = hzToMel(0);
  const melMax = hzToMel(sampleRate / 2);

  const melPoints = new Float32Array(nMels + 2);
  for (let i = 0; i < nMels + 2; i++) {
    melPoints[i] = melMin + (i * (melMax - melMin)) / (nMels + 1);
  }

  const binPoints = new Float32Array(nMels + 2);
  for (let i = 0; i < nMels + 2; i++) {
    binPoints[i] = (melToHz(melPoints[i]) * nFft) / sampleRate;
  }

  for (let m = 0; m < nMels; m++) {
    const left = binPoints[m];
    const center = binPoints[m + 1];
    const right = binPoints[m + 2];

    for (let j = 0; j < numBins; j++) {
      if (j >= left && j <= center) {
        filterBank[m * numBins + j] = (j - left) / (center - left);
      } else if (j > center && j <= right) {
        filterBank[m * numBins + j] = (right - j) / (right - center);
      }
    }
  }

  return filterBank;
}

function realFFT(input: Float32Array): Float32Array {
  const N = input.length;
  const output = new Float32Array((N / 2 + 1) * 2);

  for (let k = 0; k <= N / 2; k++) {
    let re = 0;
    let im = 0;
    for (let n = 0; n < N; n++) {
      const angle = (2 * Math.PI * k * n) / N;
      re += input[n] * Math.cos(angle);
      im -= input[n] * Math.sin(angle);
    }
    output[k * 2] = re;
    output[k * 2 + 1] = im;
  }

  return output;
}

function cosineSimilarity(a: number[], b: number[]): number {
  let dot = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : dot / denom;
}
